import React from 'react'
import { logEvent } from '@mfs/client/common/log-ga-event/logGaEvent'
import { buildShopSearchXsellUrl } from '../../../details-client/CrossSellModalAd/crossSellHelpers'
import {
  getItemID,
  getPurchaseWindowItems,
  getTripEndCityItems,
} from '@mfs/client/common/log-ga-event/logGaEvent-ecommerce'
import { determineTimeFrame } from '@mfs/client/common/log-ga-event/helpers/determineTimeFrame'
import { getItemBrand } from '@mfs/client/common/log-ga-event/helpers/listingsHelpers'
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { parseISO } from 'date-fns/parseISO'
import { startOfDay } from 'date-fns/startOfDay'

/**
 * Derives all props needed to render a recommended package-flight banner, including
 * the click handler that opens the cross-sell URL and the GA4 ecommerce data object.
 *
 * @param {Object} params
 * @param {Object} params.itinerary - Flight itinerary object from the package search response.
 * @param {Object} params.pclnBootstrapData - Bootstrap data used to construct the cross-sell redirect URL.
 * @param {Object} params.listingsMetaData - Listings-page metadata; provides `searchSessionKey` for the fly session.
 * @param {string} params.travelDate - Outbound travel date in `YYYYMMDD` format.
 * @param {number} params.lengthOfStay - Number of nights for the hotel stay portion of the package.
 * @param {number} params.bannerIndex - Zero-based position of this banner in the rendered list (mapped to `creative_slot`).
 * @param {number} params.totalPassengerCount - Total number of passengers (adults + children + infants).
 * @param {string} params.tripType - Trip type code (e.g. `'RT'` for round-trip).
 * @returns {{ totalSavings: number, ga4Data: Object, condensedBannerProps: Object } | null}
 *   Returns `null` when `itinerary` is falsy; otherwise returns the savings amount,
 *   the GA4 payload object, and the props for the condensed banner sub-component.
 */
export const generatePropsForRecommendedPkgFlight = ({
  itinerary,
  pclnBootstrapData,
  listingsMetaData,
  travelDate,
  lengthOfStay,
  bannerIndex,
  totalPassengerCount,
  tripType,
}) => {
  if (!itinerary) return null
  const { priceKey, fareBrands = [] } = itinerary

  const pkgFareBrand = fareBrands.find((fare) => fare.priceKey === priceKey)
  const pkgFareBrandPriceObject = pkgFareBrand?.price?.find((price) => price.type === 'TOTAL_PRICE')
  const totalSavings = Math.floor(pkgFareBrandPriceObject?.packagesPriceData?.totalSavings || 0)

  const slices = itinerary?.slices ?? []
  const firstSlice = slices?.[0]

  const proposalComponents = itinerary?.proposalComponents ?? []
  const flyComponent = proposalComponents.find((item) => item.type === 'FLY')
  const stayComponent = proposalComponents.find((item) => item.type === 'STAY')

  const flySessionKey = listingsMetaData?.searchSessionKey
  const flySlices = slices?.map((slice) => `${slice?.id},${slice?.sliceKey}`).join(';')

  const openXsell = () => {
    const link = buildShopSearchXsellUrl({
      pclnBootstrapData,
      xsellObject: {
        packageTypeCode: 'AH',
        numRooms: 1,
        departureAirport: firstSlice?.departInfo?.airport?.code,
        returnAirport: firstSlice?.arrivalInfo?.airport?.code,
        flyKey: flyComponent?.componentKey,
        flyItemKey: flyComponent?.itemKey,
        flyPriceKey: flyComponent?.priceKey,
        stayKey: stayComponent?.componentKey,
        stayItemKey: stayComponent?.itemKey,
        stayPriceKey: stayComponent?.priceKey,
        preferredhotelids: stayComponent?.hotelId,
        flySessionKey,
        flySlices,
      },
    })
    window.open(link, '_blank')
  }

  const ga4Data = { itinerary, totalPassengerCount, travelDate, lengthOfStay, bannerIndex, tripType }

  return {
    totalSavings,
    ga4Data,
    openXsell,
    condensedBannerProps: {
      ...generatePropsForCondensedRecommendedPkgFlight({ itinerary }),
    },
  }
}

const generatePropsForCondensedRecommendedPkgFlight = ({ itinerary }) => {
  if (!itinerary) return null

  const { priceKey, fareBrands = [] } = itinerary

  const pkgFareBrand = fareBrands.find((fare) => fare.priceKey === priceKey)
  const pkgFareBrandPriceObject = pkgFareBrand?.price?.find((price) => price.type === 'TOTAL_PRICE')
  const totalSavings = Math.floor(pkgFareBrandPriceObject?.packagesPriceData?.totalSavings || 0)

  const airline = itinerary?.airlines?.[0]

  const slices = itinerary?.slices ?? []
  const firstSlice = slices[0]
  const arrival = {
    airport: firstSlice?.arrivalInfo?.airport?.code,
    airportName: firstSlice?.arrivalInfo?.airport?.name,
    overnightDays: firstSlice?.overnightDays || 0,
    time: firstSlice?.arrivalInfo?.time?.displayTime,
  }

  const departing = {
    airport: firstSlice?.departInfo?.airport?.code,
    airportName: firstSlice?.departInfo?.airport?.name,
    time: firstSlice?.departInfo?.time?.displayTime,
  }

  return {
    heading: {
      heading: (
        <span>
          Save <strong className='font-bold'>{`$${totalSavings}`}</strong> on Flight + Hotel Deal
        </span>
      ),
    },
    body: {
      airline,
      arrival,
      departing,
      duration: firstSlice?.duration,
      isOvernight: firstSlice?.isOvernight,
      layoverInfo: firstSlice?.layoverInfo,
      merchandisingBadges: firstSlice?.merchandisingBadges,
      operatedByText: firstSlice?.displayText?.operatedByText,
    },
  }
}

const buildEcommerceItem = ({ itinerary, totalPassengerCount, travelDate, lengthOfStay, tripType }) => {
  const { priceKey, fareBrands = [], slices = [], proposalComponents = [], price = [] } = itinerary || {}
  const firstSlice = slices[0]

  const pkgFareBrand = fareBrands.find((fare) => fare.priceKey === priceKey)
  const totalPriceObj = pkgFareBrand?.price?.find((price) => price.type === 'TOTAL_PRICE')
  const totalSavings = totalPriceObj?.packagesPriceData?.totalSavings || 0
  const displayPerPersonCost = totalPriceObj?.packagesPriceData?.displayPerPersonCost || 0
  const currencyCode =
    price.find((price) => price.type === 'TOTAL_PRICE')?.currencyCode?.toLowerCase() ?? 'usd'
  const airlineCode = itinerary?.airlines?.[0]?.code
  const stops = firstSlice?.stops
  const departHour = new Date(firstSlice?.departInfo?.time?.dateTime).getHours()
  const timeFrame = determineTimeFrame(departHour)

  const departAirport = firstSlice?.departInfo?.airport?.code
  const arrivalAirport = firstSlice?.arrivalInfo?.airport?.code

  const ap = differenceInCalendarDays(parseISO(travelDate), startOfDay(new Date()))

  const starRating = getHotelStarRatingInfo(proposalComponents)
  const savingsBucket = getSavingsBucket(totalSavings)
  const itemCategory =
    savingsBucket != null && starRating != null ? `${savingsBucket}_${starRating}-star` : savingsBucket

  return {
    item_name: 'air',
    ...getItemID({ code: airlineCode, stops: stops?.toString(), timeFrame }),
    quantity: totalPassengerCount,
    price: displayPerPersonCost,
    item_brand: getItemBrand(firstSlice?.segments),
    item_category: itemCategory,
    ...(departAirport && arrivalAirport ? getTripEndCityItems([departAirport, arrivalAirport]) : {}),
    ...getPurchaseWindowItems(ap, lengthOfStay, tripType),
    currency: currencyCode,
  }
}

const ga4Attributes = {
  type: 'add_on',
  bundle_flag: 'y',
  bundle_type: 'AH',
  page_name: 'listings',
  product_name: 'air',
  promotion_name: 'hotel',
  promotion_id: 'hotel_pkg_pricing_banner',
  creative_name: 'recommended-package_save-on-flight-hotel-deal',
}

export const handleClick = (ga4Data = {}) => {
  const {
    itinerary = {},
    totalPassengerCount = 0,
    travelDate = '',
    lengthOfStay = 0,
    bannerIndex = 0,
    tripType,
  } = ga4Data
  const item = buildEcommerceItem({ itinerary, totalPassengerCount, travelDate, lengthOfStay, tripType })
  logEvent('select_promotion', {
    ...ga4Attributes,
    creative_slot: bannerIndex,
    ecommerce: {
      currency: item.currency,
      items: [item],
    },
  })
}

/**
 * Fires a `view_promotion` GA4 event when the recommended package-flight banner
 * mounts or becomes visible for the first time.
 *
 * @param {Object} [ga4Data={}]
 * @param {Object} [ga4Data.itinerary={}] - Flight itinerary object from the package search response.
 * @param {number} [ga4Data.totalPassengerCount=0] - Total number of passengers (adults + children + infants).
 * @param {number} [ga4Data.lengthOfStay=0] - Number of nights for the hotel stay portion of the package.
 * @param {string} [ga4Data.travelDate=''] - Outbound travel date in `YYYYMMDD` format.
 * @param {number} [ga4Data.bannerIndex=0] - Zero-based position of this banner in the rendered list (mapped to `creative_slot`).
 * @param {string} [ga4Data.tripType] - Trip type code (e.g. `'RT'` for round-trip).
 * @returns {void}
 */
export const handleComponentLoaded = (ga4Data = {}) => {
  const {
    itinerary = {},
    totalPassengerCount = 0,
    lengthOfStay = 0,
    travelDate = '',
    bannerIndex = 0,
    tripType,
  } = ga4Data
  const item = buildEcommerceItem({ itinerary, totalPassengerCount, travelDate, lengthOfStay, tripType })
  logEvent('view_promotion', {
    ...ga4Attributes,
    creative_slot: bannerIndex,
    ecommerce: {
      currency: item.currency,
      items: [item],
    },
  })
}

/**
 * Bucket the dollar savings amount into $50 increments up to 500+.
 * @param {number} totalSavings
 * @returns {string|null}
 */
export const getSavingsBucket = (totalSavings) => {
  if (!totalSavings || totalSavings < 0) return null
  if (totalSavings >= 500) return '500+'
  const lower = Math.floor(totalSavings / 50) * 50
  return `${lower}-${lower + 50}`
}

/**
 * Get the hotel star rating from the proposal components.
 * @param {Array<Object>} proposalComponents - `itinerary.proposalComponents`
 * @returns {number|null}
 */
export const getHotelStarRatingInfo = (proposalComponents) => {
  const stayComponent = proposalComponents?.find((item) => item.type === 'STAY') ?? {}

  return stayComponent.hotelStarRating ?? null
}
